import { HStack, Input, Text, Tooltip } from '@chakra-ui/react';
import { useState } from 'react';
import type { KeyboardEvent, ReactNode } from 'react';

export interface NumberEntryRange {
    min?: number;
    max?: number;
    /** Whole numbers only unless set; "1.5" is an error when this is false. */
    allowDecimals?: boolean;
}

/**
 * Validates typed text against a field's allowable range. Returns null when
 * the entry is acceptable, or the message to surface on the red field.
 *
 * An empty string is never an error here — whether a field may be blank is the
 * caller's business (see `allowEmpty`), not the range's.
 */
export const numberEntryError = (
    text: string,
    { min, max, allowDecimals = false }: NumberEntryRange,
): string | null => {
    const trimmed = text.trim();
    if (trimmed === '') return null;
    const parsed = Number(trimmed);
    if (!Number.isFinite(parsed)) return 'Enter a number';
    if (!allowDecimals && !Number.isInteger(parsed)) return 'Enter a whole number';
    if (min !== undefined && parsed < min) return `Must be ${min} or more`;
    if (max !== undefined && parsed > max) return `Must be ${max} or less`;
    return null;
};

export interface SettingsNumberInputProps extends NumberEntryRange {
    /** Raw text, so a half-typed value like "1" on the way to "10" survives. */
    value: string;
    onChange: (value: string) => void;
    onBlur?: () => void;
    onKeyDown?: (event: KeyboardEvent<HTMLInputElement>) => void;
    /** Shown before the field, e.g. "$". */
    prefix?: ReactNode;
    /** Shown after the field, e.g. "%" or "days". */
    unit?: ReactNode;
    width?: string;
    size?: 'xs' | 'sm' | 'md';
    placeholder?: string;
    isDisabled?: boolean;
    ariaLabel?: string;
    textAlign?: 'left' | 'right';
    /** Override the computed error, e.g. for a cross-field rule. */
    error?: string | null;
    autoFocus?: boolean;
    justify?: 'flex-start' | 'flex-end' | 'space-between';
}

/**
 * Numeric settings entry as plain text. Deliberately not `type="number"` and
 * not Chakra's NumberInput: the stepper arrows and the scroll-wheel behaviour
 * that come with those made these fields easy to change by accident. Instead
 * the field accepts any text and turns red the moment the entry falls outside
 * the field's allowable range.
 */
export const SettingsNumberInput = ({
    value,
    onChange,
    onBlur,
    onKeyDown,
    prefix,
    unit,
    width = '96px',
    size = 'sm',
    placeholder,
    isDisabled = false,
    ariaLabel,
    textAlign,
    error,
    autoFocus,
    justify,
    min,
    max,
    allowDecimals = false,
}: SettingsNumberInputProps) => {
    const message = error !== undefined ? error : numberEntryError(value, { min, max, allowDecimals });

    const field = (
        <Input
            // Text, not number: no stepper arrows and no wheel-to-change.
            type="text"
            inputMode={allowDecimals ? 'decimal' : 'numeric'}
            autoComplete="off"
            size={size}
            w={width}
            value={value}
            placeholder={placeholder}
            isDisabled={isDisabled}
            isInvalid={message !== null}
            aria-label={ariaLabel}
            aria-invalid={message !== null}
            autoFocus={autoFocus}
            textAlign={textAlign}
            onChange={(e) => onChange(e.target.value)}
            onBlur={onBlur}
            onKeyDown={onKeyDown}
        />
    );

    return (
        <HStack spacing={prefix || unit ? 2 : 0} align="center" justify={justify}>
            {prefix && (
                <Text fontSize="sm" color="gray.700">
                    {prefix}
                </Text>
            )}
            {/*
              * Always wrapped, never swapped: mounting the tooltip only on
              * error would remount the input mid-keystroke and drop focus.
              */}
            <Tooltip
                label={message ?? ''}
                isDisabled={message === null}
                placement="top"
                hasArrow
                bg="red.500"
                fontSize="sm"
            >
                {field}
            </Tooltip>
            {unit && (
                <Text fontSize="sm" color="gray.700">
                    {unit}
                </Text>
            )}
        </HStack>
    );
};

type SettingsNumberFieldBase = Omit<SettingsNumberInputProps, 'value' | 'onChange' | 'onBlur'> & {
    /** Committed value; null or undefined renders an empty field. */
    value: number | null | undefined;
    onBlur?: () => void;
};

export type SettingsNumberFieldProps = SettingsNumberFieldBase &
    (
        | {
              /** Blank clears the value (onChange receives null). Default. */
              allowEmpty?: true;
              onChange: (next: number | null) => void;
          }
        | {
              /** The field must hold a number; blank is simply not committed. */
              allowEmpty: false;
              onChange: (next: number) => void;
          }
    );

/**
 * `SettingsNumberInput` for the common case where the caller holds a number.
 * The typed text lives here, so an out-of-range or half-typed entry can sit in
 * the field in red without ever reaching the caller: only valid, in-range
 * values are committed. Blurring drops the draft, so the field always falls
 * back to showing what is actually saved.
 */
export const SettingsNumberField = ({
    value,
    onChange,
    onBlur,
    allowEmpty = true,
    ...rest
}: SettingsNumberFieldProps) => {
    const [draft, setDraft] = useState<string | null>(null);
    const committed = value == null ? '' : String(value);
    const text = draft ?? committed;
    const commit = onChange as (next: number | null) => void;
    const { min, max, allowDecimals } = rest;

    const handleChange = (next: string) => {
        setDraft(next);
        if (numberEntryError(next, { min, max, allowDecimals })) return;
        const trimmed = next.trim();
        if (trimmed === '') {
            if (allowEmpty && value != null) commit(null);
            return;
        }
        const parsed = Number(trimmed);
        if (parsed !== value) commit(parsed);
    };

    return (
        <SettingsNumberInput
            {...rest}
            value={text}
            onChange={handleChange}
            onBlur={() => {
                setDraft(null);
                onBlur?.();
            }}
        />
    );
};

export default SettingsNumberInput;
